import { useEffect, useState } from "react";
import { useSetAtom } from "lib/global-context";
import { goalAmountAtom } from "lib/atoms";
import { debounce } from "lodash";
import { cn, formatNumber } from "lib/utils";
import { Input } from "components/ui/input";
import styles from "./input-goal.module.css";

interface InputGoalProps {
  minGoal: number;
  maxGoal: number;
  headline: string;
  defaultValue: number;
}

const InputGoal = ({
  minGoal,
  maxGoal,
  headline,
  defaultValue,
}: InputGoalProps) => {
  const setGoalAmount = useSetAtom(goalAmountAtom);
  const [tempValue, setTempValue] = useState<string>(
    defaultValue.toLocaleString("en-US"),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const numericValue = Number(tempValue.replace(/,/g, ""));
    if (!isNaN(numericValue)) {
      setGoalAmount(numericValue);
    }
  }, [tempValue]);

  const validateGoal = debounce((value: string) => {
    const numericValue = Number(value.replace(/,/g, ""));
    if (numericValue < minGoal) {
      setError(
        `Input an amount between S$${minGoal.toLocaleString("en-US")} - S$${maxGoal.toLocaleString("en-US")}`,
      );
    } else if (numericValue > maxGoal) {
      setError(
        `Input an amount between S$${minGoal.toLocaleString("en-US")} - S$${maxGoal.toLocaleString("en-US")}`,
      );
    } else {
      setError(null);
      setGoalAmount(numericValue);
    }
  }, 300);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (/^[0-9,]*$/.test(value)) {
      setTempValue(formatNumber(value));
      validateGoal(value);
    }
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.headline}>{headline}</h3>
      <div className={styles.grid}>
        <div className={styles.inputWrapper}>
          {error && <span className={styles.errorLabel}>{error}</span>}
          <span className={styles.currencyLabel}>S$</span>
          <Input
            id="goalAmount"
            type="text"
            className={cn(styles.input, error && styles.errorInput)}
            value={tempValue}
            onChange={handleChange}
            inputMode="numeric"
            pattern="[0-9,]*"
            required
          />
        </div>
      </div>
    </div>
  );
};

export default InputGoal;
